<script setup>
  import { ref, onMounted, computed, nextTick } from "vue";
  import ColorCard from "./ColorCard.vue";
  import ColourFilter from "./ColourFilter.vue";
  import ColourRangeSort from "./ColourRangeSort.vue";
  import SearchComponent from "./SearchComponent.vue";

  const props = defineProps({
    newProp: null,
    model: Object
  });

  const isLoading = ref(true);
  const isTransitioning = ref(false);
  const searchTerm = ref(new URL(window.location.href).searchParams.get("q") || "");
  const trans = (key) => {
    return _.get(window.translations, key, key);
  };
  const colourRangeForSearch = ref([]);
  const originalColourRangeData = ref([]);
  const showSeries = ref(false);

  const selectedPerRow = ref(
    parseInt(new URL(window.location.href).searchParams.get("perRow") || 6)
  );

  // --- Estados do Carrossel ---
  const isCarouselOpen = ref(false);
  const selectedColorIndex = ref(0);
  const dragStartX = ref(0);
  const isDragging = ref(false);

  const sortFilters = ref({
    number: [
      { name: "01-30", value: "asc", selected: false },
      { name: "30-01", value: "desc", selected: false }
    ],
    name: [
      { name: "A-Z", value: "asc", selected: false },
      { name: "Z-A", value: "desc", selected: false }
    ],
    series: [
      { name: "SERIES 1-5", value: "asc", selected: false },
      { name: "SERIES 5-1", value: "desc", selected: false }
    ]
  });
  let timer;
  const perRowTemplate = ref("");
  const currentLayoutClass = ref("");

  // --- Funções do Carrossel ---
  const openCarousel = (color) => {
    const index = filteredColourRange.value.findIndex(c => c.id === color.id);
    if (index !== -1) {
      selectedColorIndex.value = index;
      isCarouselOpen.value = true;
      document.body.style.overflow = 'hidden';
    }
  };

  const closeCarousel = () => {
    isCarouselOpen.value = false;
    document.body.style.overflow = '';
  };

  const nextColor = () => {
    if (selectedColorIndex.value < filteredColourRange.value.length - 1) {
      selectedColorIndex.value++;
    }
  };

  const prevColor = () => {
    if (selectedColorIndex.value > 0) {
      selectedColorIndex.value--;
    }
  };

  // --- Lógica de Swipe ---
  const startDrag = (e) => {
    isDragging.value = true;
    dragStartX.value = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
  };

  const endDrag = (e) => {
    if (!isDragging.value) return;
    const endX = e.type.includes('touch') ? e.changedTouches[0].clientX : e.clientX;
    const threshold = 70;
    const diff = endX - dragStartX.value;

    if (Math.abs(diff) > threshold) {
      diff > 0 ? prevColor() : nextColor();
    }
    isDragging.value = false;
  };

  const mountGridTemplate = (perRow) => {
    isTransitioning.value = false;
    selectedPerRow.value = perRow;
    let perRowTemplateString = "";
    if (perRow === 3) {
      perRowTemplateString = "1fr 1fr 1fr";
      currentLayoutClass.value = "layout-3-columns";
    } else {
      for (let i = 0; i < perRow; i++) {
        perRowTemplateString += "1fr ";
      }
      currentLayoutClass.value = `layout-${perRow}-columns`;
    }
    perRowTemplate.value = perRowTemplateString.trim();

    const url = new URL(window.location.href);
    if (perRow) {
      url.searchParams.set("perRow", perRow);
    } else {
      url.searchParams.delete("perRow");
    }
    window.history.replaceState(null, "", url.toString());
    nextTick(() => {
      setTimeout(() => {
        isTransitioning.value = true;
      }, 150);
    });
  };

  const filteredColourRange = computed(() => {
    const query = searchTerm.value.toLowerCase().trim();
    const sourceData = colourRangeForSearch.value;
    if (!query) {
      return sourceData;
    }
    return sourceData.filter(item =>
      (item.name && item.name.toLowerCase().includes(query)) ||
      (item.series && item.series.toLowerCase().includes(query))
    );
  });


  const groupedColourRange = computed(() => {
    const dataToGroup = filteredColourRange.value;
    const groups = [];
    for (let i = 0; i < dataToGroup.length; i += 2) {
      groups.push(dataToGroup.slice(i, i + 2));
    }
    return groups;
  });

  const debounceFunc = function(callback, delay) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      callback();
    }, delay);
  };


  const handleFilters = (filter) => {
    const url = new URL(window.location.href);
    url.searchParams.delete("order[number]");
    url.searchParams.delete("order[name]");
    url.searchParams.delete("order[series]");
    window.history.replaceState(null, "", url.toString());

    if (filter?.value?.value) {
      handleUpdateQuery("order[" + filter.type + "]", filter.value.value);
    } else {
      fetchColourRangeData();
    }
  };

  const handleUpdateQuery = (queryString, newTerm) => {
    const url = new URL(window.location.href);
    if (newTerm) {
      url.searchParams.set(queryString, newTerm);
    } else {
      url.searchParams.delete(queryString);
    }
    window.history.replaceState(null, "", url.toString());


    if (queryString === "q") {
      searchTerm.value = newTerm || "";
      isTransitioning.value = false;
      nextTick(() => {
        setTimeout(() => {
          isTransitioning.value = true;
        }, 100);
      });
    } else {
      debounceFunc(fetchColourRangeData, 250);
    }
  };

  const fetchAllColourRangeData = async () => {
    try {
      const response = await fetch("/api/topcer/colorRageList");
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      originalColourRangeData.value = data;
    } catch (error) {
      console.error("Erro ao buscar dados da API:", error);
    }
  };

  const fetchColourRangeData = async () => {
    isLoading.value = true;
    isTransitioning.value = false;

    try {
      const urlPage = new URL(window.location.href);
      let apiUrl = new URL(window.location.origin + "/api/topcer/colorRageList");

      showSeries.value = false;
      for (const param of urlPage.searchParams) {
        if (param[1] == null || param[1] === "null") {
          continue;
        }
        if (param[0] === "order[series]") {
          showSeries.value = true;
        }
        apiUrl.searchParams.append(param[0], param[1]);
      }
      const response = await fetch(apiUrl.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      colourRangeForSearch.value = data;
    } catch (error) {
      console.error("Erro ao buscar dados da API:", error);
    } finally {
      setTimeout(() => {
        isLoading.value = false;
        isTransitioning.value = true;
      }, 300);
    }
  };

  const findSeries = (idx) => {
    const data = filteredColourRange.value;
    if (idx === 0) {
      return true;
    }
    return data[idx - 1]?.series !== data[idx]?.series;
  };


  onMounted(() => {
    mountGridTemplate(selectedPerRow.value);
    fetchColourRangeData();
    fetchAllColourRangeData();

    window.addEventListener('keydown', (e) => {
      if (!isCarouselOpen.value) return;
      if (e.key === 'ArrowRight') nextColor();
      if (e.key === 'ArrowLeft') prevColor();
      if (e.key === 'Escape') closeCarousel();
    });
  });
</script>

<template>
  <Transition name="fade-overlay">
    <div v-if="isLoading" id="loading-overlay">
      <div class="spinner"></div>
    </div>
  </Transition>
  <div class="container-colors">
    <div class="content-search">
      <search-component
        :searchOptions="originalColourRangeData"
        @update:searchTerm="(term)=>{handleUpdateQuery('q', term)}"
      ></search-component>
    </div>
    <div class="content-filter">
      <div class="filters">
        <ColourFilter
          :sortFilters="sortFilters"
          @update:sortOrder="handleFilters"
        ></ColourFilter>
      </div>
      <div class="grid-view">
        <ColourRangeSort
          @change-per-row="mountGridTemplate"
          :newProp="newProp"
          :selectedLayout="selectedPerRow"
        ></ColourRangeSort>
      </div>
    </div>

    <div class="container-downloads">
      <div v-for="doc in model.documents">
        <div v-for="file in doc.files">
          <a class="button-btn button-next" :href="file.fullUrl" :download="file.title">
            {{ trans("Download") }}
          </a>
        </div>
      </div>
    </div>

    <div
      class="container-cards"
      :class="currentLayoutClass">
      <template v-if="currentLayoutClass === 'layout-3-columns'">
        <template v-for="(group, groupIndex) in groupedColourRange" :key="groupIndex">
          <template v-if="findSeries(groupIndex * 2) && showSeries">
            <div class="title-full-width">
              <h1 class="title-serie">{{ group[0].series }}</h1>
              <hr/>
            </div>
          </template>
          <div
            class="card-pair-wrapper">
            <template v-for="(colorItem, idx) in group" :key="colorItem.id">
              <ColorCard
                :color="colorItem"
                @select="openCarousel"
              />
            </template>
          </div>
        </template>
      </template>

      <template v-else>
        <template v-for="(colorItem, idx) in filteredColourRange" :key="colorItem.id">
          <template v-if="findSeries(idx) && showSeries">
            <div class="title-full-width">
              <h1 class="title-serie">{{ colorItem.series }}</h1>
              <hr />
            </div>
          </template>
          <ColorCard
            :color="colorItem"
            @select="openCarousel"
          />
        </template>
      </template>

      <div v-if="!filteredColourRange.length && !isLoading" class="title-full-width">
        <p>{{ trans("No colors were found for your search.") }}</p>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="fade">
        <div v-if="isCarouselOpen" class="modal-carousel" @click.self="closeCarousel">
          <button class="close-btn" @click="closeCarousel">
            <svg data-v-4aa747e0="" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 12 12"
                 fill="none">
              <path data-v-4aa747e0="" d="M11 1L1 11" stroke="#333333" stroke-miterlimit="10"></path>
              <path data-v-4aa747e0="" d="M1 1L11 11" stroke="#333333" stroke-miterlimit="10"></path>
            </svg>
          </button>

          <div class="carousel-container">
            <button class="nav-btn" @click="prevColor" :disabled="selectedColorIndex === 0">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="lucide lucide-chevron-left-icon lucide-chevron-left">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </button>

            <div class="carousel-main-content"
                 @mousedown="startDrag" @mouseup="endDrag" @mouseleave="isDragging = false"
                 @touchstart="startDrag" @touchend="endDrag">

              <div v-if="filteredColourRange[selectedColorIndex]" class="carousel-card-inner">
                <img :src="filteredColourRange[selectedColorIndex].featured_image" class="carousel-img" draggable="false" />
                <div class="carousel-info">
                  <span>{{ String(filteredColourRange[selectedColorIndex].value).padStart(2, '0') }} -
                  {{ filteredColourRange[selectedColorIndex].name }}
                  </span>
                  <p>{{ filteredColourRange[selectedColorIndex].series }}</p>
                </div>
              </div>
            </div>

            <button class="nav-btn" @click="nextColor" :disabled="selectedColorIndex === filteredColourRange.length - 1">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="lucide lucide-chevron-right-icon lucide-chevron-right">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
  .container-cards.layout-6-columns :deep(.card) {
    gap: 20px;
    width: 100%;
  }

  .container-cards.layout-6-columns :deep(.card-content) {
    width: 150px;
    height: 150px;
  }

  .container-cards.layout-3-columns :deep(.card) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .container-cards.layout-3-columns :deep(.title-card) {
    text-align: left;
    width: 100%;
  }











  .fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
  .fade-enter-from, .fade-leave-to { opacity: 0; }

  @media (max-width: 768px) {
    .nav-btn { display: none; }
    .carousel-info h2 { font-size: 1.6rem; }
  }
</style>
